// Array.prototype.toSpliced ships only in Safari/iOS 16.4, while contact-admin supports Safari/iOS 15
// (browserslist `safari >= 15` / `ios >= 15`, Vite target `safari15`, and no core-js / plugin-legacy to
// polyfill built-in methods). Keep this copy-then-splice implementation so non-mutating array updates
// remain safe at the declared browser floor — do not "simplify" it back to toSpliced.
//
// The copy step is `Array.from({ length }, indexed-read)` for a specific reason, and each of the obvious
// shorter spellings is wrong in a different way. The built-in reads INDEXED properties and writes every
// index of a fresh array, so it both ignores a custom `Symbol.iterator` and densifies holes:
//
//   items.slice()                              holes stay holes            (diverges: sparse input)
//   Array.from(items)                          consumes Symbol.iterator    (diverges: custom iterator)
//   Array.from({ length }, (_, i) => items[i]) indexed reads, dense        (matches the built-in)
//
// `deleteCount` is deliberately REQUIRED, which is one intentional divergence. `arr.toSpliced(2)` deletes
// through the end while `arr.toSpliced(2, undefined)` deletes nothing, so an arity-faithful shim could only
// tell them apart via `arguments.length` — which forces optional parameters and `as number` casts in the one
// helper that must not be sloppy. Requiring the argument turns that ambiguity into a compile error at the
// call site instead, and all callers pass an explicit count. For delete-through-the-end, write
// `items.slice(0, start)`.
//
// One further accepted divergence: the built-in never reads the elements it is about to delete, whereas any
// copy-then-splice reads the whole source first. A throwing getter inside the deleted range would therefore
// throw here and not natively. That is inherent to this approach rather than new (plain `slice()` behaves the
// same), and no caller stores accessors on an array, so it is accepted rather than worked around by
// re-deriving splice's own index arithmetic — which is the part of this fix that must stay provably identical.
export function toSplicedCompat<T>(items: readonly T[], start: number, deleteCount: number, ...inserted: T[]): T[] {
	const copy = Array.from({ length: items.length }, (_unused, index) => items[index]);
	copy.splice(start, deleteCount, ...inserted);
	return copy;
}
